import { isoLangs } from '@zedoc/iso-langs';

const displayNamesByLocale = new Map();

const getDisplayNames = (uiLanguage) => {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
    return null;
  }
  const locale = uiLanguage || 'en';
  if (!displayNamesByLocale.has(locale)) {
    let displayNames = null;
    try {
      displayNames = new Intl.DisplayNames([locale], {
        type: 'language',
        fallback: 'none',
      });
    } catch (error) {
      // Unsupported or malformed locale: fall back to the English names.
      displayNames = null;
    }
    displayNamesByLocale.set(locale, displayNames);
  }
  return displayNamesByLocale.get(locale);
};

/**
 * The name of a language (ISO 639 code such as "fr" or "zh-Hant") in the
 * user's UI language, capitalised for use as an option label: "Français"
 * when the UI is French, "French" when it is English, "フランス語" in
 * Japanese. Falls back to the English name from `@zedoc/iso-langs`, then to
 * the code itself, when `Intl.DisplayNames` is unavailable or does not know
 * the code. (TECH-3695 QA-3 TC48: the per-row language picker on the
 * Documents page showed English names whatever the UI language.)
 */
export const getLanguageDisplayName = (code, uiLanguage) => {
  const fallback = (code && isoLangs[code]?.name) || code || '';
  if (!code) {
    return fallback;
  }
  const displayNames = getDisplayNames(uiLanguage);
  if (!displayNames) {
    return fallback;
  }
  let name;
  try {
    name = displayNames.of(code);
  } catch (error) {
    // RangeError on a code that is not a structurally valid language tag.
    return fallback;
  }
  if (!name) {
    return fallback;
  }
  return name.charAt(0).toLocaleUpperCase(uiLanguage || 'en') + name.slice(1);
};
